import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { brandInfo, faqs } from '@/mocks/brand';
import { articleThumbnails } from '@/mocks/thumbnails';

function HeroSection() {
  const { t } = useTranslation();
  return (
    <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Warm%20golden%20abstract%20geometric%20pattern%20with%20soft%20gradients%2C%20luxurious%20minimalist%20background%2C%20elegant%20Chinese%20modern%20corporate%20style%2C%20subtle%20light%20beams%2C%20refined%20professional%20atmosphere%2C%20warm%20amber%20and%20cream%20tones%2C%20high-end%20institutional%20feel&width=1800&height=900&seq=hero-main-2026&orientation=landscape"
        alt="金宝博品牌背景"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
      <div className="relative z-10 w-full px-4 md:px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-background-50 leading-tight mb-5">
            金宝博 · 您信赖的首选平台
          </h1>
          <p className="text-base md:text-lg text-background-100/90 max-w-2xl mx-auto leading-relaxed mb-8">
            安全、便捷、专业的一站式服务平台，为全球数百万用户提供卓越体验。无论您是新用户还是老会员，金宝博都值得您的信赖。
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/about"
              className="px-8 py-3 bg-primary-500 text-background-50 font-semibold rounded-md hover:bg-primary-600 transition-all text-sm whitespace-nowrap cursor-pointer"
            >
              立即注册
            </Link>
            <Link
              to="/category/pingtai-jieshao"
              className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 font-medium rounded-md hover:bg-background-50/30 transition-all text-sm whitespace-nowrap cursor-pointer"
            >
              了解更多
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  const stats = [
    { value: '500万+', label: '服务用户', icon: 'ri-user-star-line' },
    { value: '98.5%', label: '用户满意度', icon: 'ri-heart-line' },
    { value: '30+', label: '行业大奖', icon: 'ri-award-line' },
    { value: '50+', label: '覆盖国家/地区', icon: 'ri-global-line' },
  ];

  return (
    <section className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                <i className={`${stat.icon} text-2xl text-primary-500`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: 'ri-shield-check-line',
      title: '安全可靠',
      description: '银行级安全防护体系，多重身份验证与数据加密，全方位保障您的账户与资金安全。',
    },
    {
      icon: 'ri-flashlight-line',
      title: '便捷高效',
      description: '简洁直观的操作流程，充值提现快速到账，让您随时随地享受流畅的服务体验。',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '专业服务',
      description: '7×24小时专业客服团队，平均30秒响应，多语言支持，随时为您排忧解难。',
    },
    {
      icon: 'ri-vip-crown-line',
      title: '会员尊享',
      description: '五级会员体系，丰富专属权益，VIP客户经理一对一服务，尊享极致体验。',
    },
  ];

  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            为什么选择金宝博
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            四大核心优势，为您打造极致的使用体验
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="bg-background-50 rounded-lg p-6 md:p-8 text-center group hover:bg-primary-50/30 transition-colors"
            >
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 group-hover:bg-accent-200 transition-colors">
                <i className={`${feature.icon} text-2xl text-accent-500`}></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoriesSection() {
  return (
    <section className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            分类浏览
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            按主题探索金宝博的丰富内容
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`/category/${category.id}`}
              className="group bg-background-100 rounded-lg p-6 md:p-7 hover:bg-primary-50/40 transition-colors cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 group-hover:bg-accent-200 transition-colors">
                  <i className={`${category.icon} text-xl text-accent-500`}></i>
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-3">
                    {category.description}
                  </p>
                  <span className="text-xs text-foreground-400">
                    {category.articleCount} 篇文章
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestArticlesSection() {
  const latestArticles = articles.slice(0, 6);

  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-12 md:mb-16">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              最新文章
            </h2>
            <p className="text-sm md:text-base text-foreground-500">
              了解金宝博的最新动态与实用指南
            </p>
          </div>
          <Link
            to="/category/pingtai-jieshao"
            className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            查看全部 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {latestArticles.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="group bg-background-50 rounded-lg overflow-hidden hover:bg-primary-50/20 transition-colors cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={articleThumbnails[article.id]}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2 flex-wrap">
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                  {article.excerpt}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            用户口碑
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            数百万用户的选择与信赖
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {brandInfo.testimonials.slice(0, 6).map((testimonial, index) => (
            <div key={index} className="bg-background-100 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <span className="text-sm font-semibold text-accent-600">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-900">
                    {testimonial.name}
                  </div>
                  <div className="text-xs text-foreground-400">{testimonial.role}</div>
                </div>
              </div>
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`text-xs ${i < testimonial.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                  ></i>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {testimonial.content}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            常见问题
          </h2>
          <p className="text-sm md:text-base text-foreground-500">
            快速解答您的疑问
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {faqs.slice(0, 6).map((faq, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-lg overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="text-sm font-medium text-foreground-900 pr-4">
                  {faq.question}
                </span>
                <i
                  className={`text-sm text-foreground-400 transition-transform flex-shrink-0 ${
                    openIndex === index ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                  }`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative w-full py-16 md:py-24 overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Warm%20elegant%20abstract%20golden%20waves%20background%2C%20luxurious%20minimalist%20texture%2C%20soft%20gradient%20lighting%2C%20premium%20corporate%20feel%2C%20inviting%20warm%20atmosphere%2C%20refined%20Chinese%20modern%20aesthetic&width=1600&height=600&seq=cta-home-2026&orientation=landscape"
        alt="加入金宝博"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-black/45"></div>
      <div className="relative z-10 max-w-2xl mx-auto px-4 md:px-6 text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
          立即加入金宝博
        </h2>
        <p className="text-sm md:text-base text-background-100/80 mb-8 max-w-md mx-auto leading-relaxed">
          新用户注册即享专属礼包，开启您的金宝博之旅
        </p>
        <Link
          to="/about"
          className="inline-block px-10 py-3.5 bg-primary-500 text-background-50 font-semibold rounded-md hover:bg-primary-600 transition-all text-sm whitespace-nowrap cursor-pointer"
        >
          免费注册
        </Link>
      </div>
    </section>
  );
}

import React from 'react';

export default function Home() {
  const schemaData = {
    Organization: {
      name: '金宝博',
      url: 'https://www.jinbaobo.com/',
      description: '金宝博是行业领先的一站式服务平台，为您提供安全、便捷、专业的一站式体验。',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-8888',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
    WebSite: {
      name: '金宝博',
      url: 'https://www.jinbaobo.com/',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.jinbaobo.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    WebPage: {
      name: '金宝博官方落地页 | 金宝博品牌介绍与服务指南',
      description: '金宝博是您信赖的首选平台，全面介绍金宝博的核心服务、品牌优势、用户口碑与注册流程。',
      url: 'https://www.jinbaobo.com/',
    },
    FAQPage: {
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" data={schemaData.Organization} />
      <SchemaOrg type="WebSite" data={schemaData.WebSite} />
      <SchemaOrg type="WebPage" data={schemaData.WebPage} />
      <SchemaOrg type="FAQPage" data={schemaData.FAQPage} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <StatsSection />
          <FeaturesSection />
          <CategoriesSection />
          <LatestArticlesSection />
          <TestimonialsSection />
          <FAQSection />
          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
}